import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, X } from "lucide-react";

const SECTIONS = [
  { key: "well_self", title: "When I'm well", desc: "How I feel, look and behave when I'm well." },
  { key: "triggers", title: "Triggers", desc: "Events or circumstances that can be unsettling." },
  { key: "early_warning", title: "Early warning signs", desc: "Small changes that tell me things are starting to slip." },
  { key: "when_breaking_down", title: "When things are breaking down", desc: "Signs I'm in real trouble and need immediate help." },
  { key: "crisis_plan", title: "Crisis plan", desc: "What I want others to do for me when I can't help myself." },
  { key: "post_crisis_plan", title: "Post-crisis plan", desc: "How I want to be supported after a crisis." },
];

export default function Wrap() {
  const [plan, setPlan] = useState(null);
  const [drafts, setDrafts] = useState({});

  useEffect(() => { api.get("/wrap").then((r) => setPlan(r.data)); }, []);

  if (!plan) return <div className="text-slate-500" data-testid="wrap-loading">Loading…</div>;

  const add = (key) => {
    const v = (drafts[key] || "").trim();
    if (!v) return;
    setPlan({ ...plan, [key]: [...(plan[key] || []), v] });
    setDrafts({ ...drafts, [key]: "" });
  };
  const remove = (key, idx) => {
    const arr = [...(plan[key] || [])];
    arr.splice(idx, 1);
    setPlan({ ...plan, [key]: arr });
  };
  const save = async () => {
    try { await api.put("/wrap", plan); toast.success("WRAP saved"); } catch { toast.error("Could not save"); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="wrap-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Wellness Recovery Action Plan</h1>
          <p className="text-slate-600 mt-2 max-w-xl">Mary Ellen Copeland's WRAP framework — a companion to your Stanley &amp; Brown safety plan.</p>
        </div>
        <button data-testid="wrap-save" onClick={save} className="bg-teal-700 hover:bg-teal-800 text-white rounded-xl px-5 py-2.5 font-semibold flex items-center gap-2">
          <Save className="w-4 h-4" /> Save WRAP
        </button>
      </header>

      <div className="space-y-4">
        {SECTIONS.map((s) => {
          const items = plan[s.key] || [];
          return (
            <div key={s.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid={`wrap-${s.key}`}>
              <h2 className="font-display text-lg font-bold text-slate-900">{s.title}</h2>
              <p className="text-sm text-slate-500 mt-0.5">{s.desc}</p>
              <ul className="mt-3 space-y-2">
                {items.map((it, idx) => (
                  <li key={idx} className="flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2">
                    <span className="text-sm text-slate-800">{it}</span>
                    <button data-testid={`wrap-remove-${s.key}-${idx}`} onClick={() => remove(s.key, idx)} className="text-slate-400 hover:text-red-600">
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2 mt-3">
                <input
                  data-testid={`wrap-input-${s.key}`}
                  value={drafts[s.key] || ""}
                  onChange={(e) => setDrafts({ ...drafts, [s.key]: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && add(s.key)}
                  className="flex-1 border border-slate-300 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  placeholder="Add an entry…"
                />
                <button data-testid={`wrap-add-${s.key}`} onClick={() => add(s.key)} className="bg-teal-700 hover:bg-teal-800 text-white rounded-xl px-3 py-2 flex items-center gap-1 text-sm">
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
